import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Clock, CheckCircle, XCircle, Eye, Package, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../config/api';

const STATUS_FLOW = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered'];
const STATUS_LABELS = {
  pending: 'Pending', confirmed: 'Confirmed', preparing: 'Preparing', ready: 'Ready',
  out_for_delivery: 'Out for Delivery', delivered: 'Delivered', cancelled: 'Cancelled', rejected: 'Rejected'
};
const STATUS_COLORS = {
  pending: '#FFC107', confirmed: '#2196F3', preparing: '#FF9800', ready: '#9C27B0',
  out_for_delivery: '#00BCD4', delivered: '#4CAF50', cancelled: '#F44336', rejected: '#F44336'
};

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      const res = await api.get('/orders.php');
      if (res.data.success) setOrders(res.data.data);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = STATUS_COLORS[status] || '#999';
    return { bg: s + '20', color: s, dot: s };
  };

  const getProgressPercent = (status) => {
    if (!STATUS_FLOW.includes(status)) return status === 'cancelled' || status === 'rejected' ? 0 : 10;
    return Math.round((STATUS_FLOW.indexOf(status) + 1) / STATUS_FLOW.length * 100);
  };

  return (
    <div style={{ backgroundColor: '#faf8f5', minHeight: '100vh', paddingTop: '100px', paddingBottom: '60px' }}>
      <div className="container">
        <div className="mb-4">
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '2rem', color: '#1A1A1A', marginBottom: '4px' }}>
            My Orders
          </h2>
          <p style={{ color: '#999', fontSize: '0.9rem' }}>Track and review your past orders</p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#999' }}>
            <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTopColor: '#D4AF37', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }}></div>
            <p>Loading your orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Package size={48} color="#ddd" style={{ marginBottom: '16px' }} />
            <h4 style={{ fontFamily: 'Playfair Display, serif', color: '#999', marginBottom: '8px' }}>No orders yet</h4>
            <p style={{ color: '#bbb', marginBottom: '20px' }}>Place your first order from our menu</p>
            <Link to="/menu" className="btn btn-gold">Browse Menu</Link>
          </div>
        ) : (
          <div className="row g-4">
            {orders.map(order => {
              const badge = getStatusBadge(order.status);
              const progress = getProgressPercent(order.status);
              return (
                <div key={order.order_id} className="col-12">
                  <div style={{
                    backgroundColor: '#fff', borderRadius: '16px', overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.05)', border: '1px solid #EAEDF1'
                  }}>
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid #f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                        <ShoppingCart size={20} color="#D4AF37" />
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: '1rem', color: '#333' }}>Order #{order.order_id}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: badge.bg, color: badge.color, padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600 }}>
                              {STATUS_LABELS[order.status] || order.status}
                            </span>
                          </div>
                          <span style={{ color: '#999', fontSize: '0.82rem', marginTop: '4px', display: 'block' }}>
                            {new Date(order.order_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <span style={{ fontWeight: 700, fontSize: '1.05rem', color: '#D4AF37' }}>
                            UGX {Number(order.total_amount).toLocaleString()}
                          </span>
                          {order.eta_minutes && ['confirmed', 'preparing', 'ready', 'out_for_delivery'].includes(order.status) && (
                            <span style={{ fontSize: '0.75rem', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Clock size={12} /> {order.eta_minutes} min remaining
                            </span>
                          )}
                        </div>
                        <Link to={`/track-order/${order.order_id}`} className="btn btn-outline-gold" style={{ padding: '8px 16px', fontSize: '0.82rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          Track <ChevronRight size={14} />
                        </Link>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div style={{ padding: '0 24px', backgroundColor: '#FAFAFA' }}>
                      <div style={{ height: '4px', backgroundColor: '#E0E0E0', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${progress}%`, backgroundColor: STATUS_COLORS[order.status] || '#999', borderRadius: '2px', transition: 'width 0.5s ease' }} />
                      </div>
                    </div>

                    {expandedOrder === order.order_id && (
                      <div style={{ padding: '20px 24px', backgroundColor: '#FAFAFA' }}>
                        {order.items?.map((item, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.88rem' }}>
                            <span style={{ color: '#555' }}>{item.item_name} <span style={{ color: '#999' }}>×{item.quantity}</span></span>
                            <span style={{ fontWeight: 600, color: '#333' }}>UGX {Number(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '12px', borderTop: '2px solid #e0e0e0' }}>
                          <span style={{ fontWeight: 700, color: '#1A1A1A' }}>Total</span>
                          <span style={{ fontWeight: 700, color: '#D4AF37', fontSize: '1.1rem' }}>UGX {Number(order.total_amount).toLocaleString()}</span>
                        </div>
                        <div style={{ marginTop: '12px', fontSize: '0.82rem', color: '#666' }}>
                          <strong>Payment:</strong> {order.payment_method} | <strong>Address:</strong> {order.delivery_address || 'N/A'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default MyOrders;
